import { addSeconds, subDays } from "date-fns";
import * as R from "remeda";

import { Study, LinkedStudy, LinkedMainStudy, StudyGroup } from "@shared/types";

export function isExpiredStudy(study: LinkedMainStudy): boolean {
  if (!study.expiresInSeconds) return false;

  const createdAt = new Date(study.createdAt);
  const expiresAt = addSeconds(createdAt, study.expiresInSeconds);
  const now = new Date();

  return now > expiresAt;
}

export function effectiveStudy(study: Study | LinkedStudy): LinkedMainStudy {
  if ("mainStudy" in study) {
    return study.mainStudy && !study.overrideMain ? study.mainStudy : study;
  }
  return study;
}

export function linkedStudyChatMessageExpirationDate(linkedStudy: LinkedStudy) {
  const mainStudy = effectiveStudy(linkedStudy);

  return typeof mainStudy.baselineChatHistoryWindowDays === "number"
    ? subDays(
        new Date(linkedStudy.enrolledAt || linkedStudy.createdAt),
        mainStudy.baselineChatHistoryWindowDays,
      )
    : null;
}

export function isExpiredLinkedStudy(study: LinkedStudy): boolean {
  return isExpiredStudy(effectiveStudy(study));
}

export function isClosedLinkedStudy(study: LinkedStudy): boolean {
  return !!study.closed || !!study.mainStudy?.closed;
}

export function isNonValidatedLinkedStudy(study: LinkedStudy): boolean {
  return !!effectiveStudy(study).skipValidation;
}

export function isReportShownLinkedStudy(study: LinkedStudy): boolean {
  return !!effectiveStudy(study).showReport;
}

export function groupStudies(studies: Study[]): StudyGroup[] {
  return R.pipe(
    studies,
    R.groupBy((s) => s.id),
    R.entries(),
    R.map(([id, rows]) => {
      const [mains, participants] = R.partition(rows, (s) => !s.participantId);
      return {
        id: id,
        mainStudy: mains[0],
        participants: R.sortBy(participants, (s) => s.participantId ?? ""),
        totalSubmissions: R.sumBy(rows, (s) => s.submissionCount ?? 0),
      };
    }),
    R.filter((g): g is StudyGroup => g.mainStudy !== undefined),
    R.sortBy([(g) => g.mainStudy.createdAt, "desc"]),
  );
}
